<template>
  <validation-observer ref="observer" tag="div" class="add-edit-user-form">
    <oie-form-wrapper class="add-edit-user-form__form" @submit="handleSubmit">
      <div v-if="user.isDeleted" class="add-edit-user-form__section">
        <span>{{ t('myUsersUndeleteUserLabel') }}</span>
        <oie-base-action theme="destructive" @click.prevent="handleUndeleteUser">
          {{ t('myUsersUndeleteUserButton') }}
        </oie-base-action>
      </div>

      <div v-bind="deletedInertAttribute" class="add-edit-user-form__inert">
        <div class="add-edit-user-form__section">
          <span id="add-edit-user-form-status">{{ t('myUsersUserIsActiveLabel2') }}</span>
          <oie-base-switch
            v-model="isActive"
            :disabled="isCurrentUser"
            aria-labelledby="add-edit-user-form-status"></oie-base-switch>
        </div>
      </div>
      <div v-bind="inertAttribute" class="add-edit-user-form__inert">
        <div class="add-edit-user-form__section">
          <span id="add-edit-user-form-admin">{{ t('myUsersIsAdministrator2') }}</span>
          <oie-base-switch
            v-model="user.isUserAdmin"
            :disabled="isCurrentUser"
            aria-labelledby="add-edit-user-form-admin"></oie-base-switch>
        </div>
      </div>
      <div v-bind="inertAttribute" class="add-edit-user-form__inert">
        <div class="add-edit-user-form__section">
          <span>{{ t('myUsersHandlePasswordLabel2') }}</span>
          <oie-base-action theme="outlined" @click.prevent="handleSendNewPassword">
            {{ t('myUsersSendNewPassword2') }}
          </oie-base-action>
        </div>
      </div>
      <div v-if="!user.isDeleted" class="add-edit-user-form__section">
        <span>{{ t('myUsersDeleteUserLabel2') }}</span>
        <oie-base-action theme="destructive" :disabled="isCurrentUser" @click.prevent="handleDeleteUser">
          {{ t('myUsersDeleteUserButton2') }}
        </oie-base-action>
      </div>
      <div v-bind="inertAttribute" class="add-edit-user-form__inert">
        <div>
          <div class="input-column__container">
            <h2 class="h3-semi-bold">
              {{ t('myUsersUserDetailsHeader2') }}
            </h2>
            <validation-provider v-slot="{errors}" slim rules="required">
              <oie-base-input
                v-model="user.firstName"
                :label-text="`${tStatic('firstNameLabel')}*`"
                :placeholder="tStatic('firstNamePlaceholder')"
                :error-messages="errors"></oie-base-input>
            </validation-provider>
            <validation-provider v-slot="{errors}" slim rules="required">
              <oie-base-input
                v-model="user.lastName"
                :label-text="`${tStatic('lastNameLabel')}*`"
                :placeholder="tStatic('lastNamePlaceholder')"
                :error-messages="errors"></oie-base-input>
            </validation-provider>
            <validation-provider v-slot="{errors}" rules="required|numeric" slim>
              <oie-base-input
                id="my-pages-company-number"
                v-model="phoneNumberProxy"
                :error-messages="errors"
                :label-text="`${tStatic('phoneLabel')}*`"
                :placeholder="tStatic('phonePlaceholderLabel')"
                :prefix-value="activeCountry.callingCode">
              </oie-base-input>
            </validation-provider>
            <validation-provider slim rules="required">
              <oie-base-input v-model="user.email" :label-text="`${tStatic('email')}*`" :is-readonly="true"></oie-base-input>
            </validation-provider>
          </div>
        </div>
      </div>
      <oie-action-wrapper v-if="!user.isDeleted" class="add-edit-user-form__actions">
        <oie-base-action theme="skeleton" @click="() => $emit('cancel')">
          {{ tStatic('cancel') }}
        </oie-base-action>
        <oie-base-action type="submit">
          {{ tStatic('save') }}
        </oie-base-action>
      </oie-action-wrapper>
    </oie-form-wrapper>
  </validation-observer>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import myPages from '@/mixins/my-pages'
import httpService from '@/core/http-service'
import utils from '@/core/utils'

export default {
  mixins: [myPages],
  data() {
    return {
      user: {
        userId: '',
        name: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '',
        isUserAdmin: false,
        isDisabled: false,
        isDeleted: false,
        labels: [],
        isAwaitingAcceptInvite: null
      }

    }
  },
  computed: {
    ...mapGetters('myUsers', ['currentUser']),
    isActive: {
      get() {
        return !this.user.isDisabled
      },
      set(val) {
        this.user.isDisabled = !val
      }
    },
    isCurrentUser() {
      return this.get(this.clientModel, 'user.userName') === this.user.userId && this.clientModel.impersonating === null
    },
    deletedInertAttribute() {
      return this.user.isDeleted ? { inert: true } : undefined
    },
    inertAttribute() {
      return !this.isActive || this.user.isDeleted ? { inert: true } : undefined
    },
    activeCountry() {
      return this.clientModel.countryList.find(c => c.value === this.user.countryCode)
    },
    phoneNumberProxy: {
      get() {
        return utils.extractPhoneNumberFromFormating(this.user.phone, this.activeCountry.callingCode)
      },
      set(val) {
        this.user.phone = utils.savePhoneNumberWithFormating(val, this.activeCountry.callingCode)
      }
    }
  },
  beforeMount() {
    if (this.currentUser) {
      this.user = _.merge(this.user, this.currentUser)
    }
  },
  methods: {
    ...mapActions('myUsers', ['updateUser', 'deleteUser', 'undeleteUser']),
    async handleSubmit() {
      const isValid = await this.$refs.observer.validate()
      if (isValid) {
        await this.updateUser(this.user)
        this.goToSubPage(this.navigationOptions.oieMyUsers)
      }
    },
    handleDeleteUser() {
      this.publish('modal.show.generic', {
        header: `${this.t('deleteUserModalHeading')}: ${this.user.name}`,
        text: this.t('deleteUserModalText'),
        buttons: [ {
          text: this.tStatic('genericDelete'),
          usePrimaryButton: true,
          handler: async () => {
            await this.deleteUser(this.user)
            this.goToSubPage(this.navigationOptions.oieMyUsers)
            this.publish('modal.hide.generic')
          }
        }]
      })
    },
    async handleSendNewPassword() {
      await httpService.post('auth/myusers/sendForgetPassword', this.user)
    },
    handleUndeleteUser() {
      this.publish('modal.show.generic', {
        header: `${this.t('myUsersUndeleteUserModalHeading')}: ${this.user.name}`,
        text: this.t('myUsersUndeleteUserModalText'),
        buttons: [ {
          text: this.tStatic('genericUndelete'),
          usePrimaryButton: true,
          handler: async () => {
            await this.undeleteUser(this.user)
            this.goToSubPage(this.navigationOptions.oieMyUsers)
            this.publish('modal.hide.generic')
          }
        }]
      })
    }
  }
}
</script>

<style lang="scss">
.add-edit-user-form {
  container-name: add-edit-user-form;
  container-type: inline-size;
}

.add-edit-user-form__inert[inert] {
  opacity: 0.6;
}

.add-edit-user-form__section {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: measure(half);
  flex-direction: column;
  padding: measure(half) 0;
  border-bottom: 1px solid color(gray-4);

  @container add-edit-user-form (min-width: 600px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: measure(full) 0;
  }
}

.add-edit-user-form .input-column__container h2,
.add-edit-user-form__actions {
  margin-top: measure(half);
}
</style>
